import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Database, HardDrive, Loader2, Upload, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const RESTORABLE_TABLES = [
  'services',
  'resources',
  'footer_logos',
  'blog_posts',
  'testimonials',
  'email_subscribers',
  'leads',
  'assessment_results',
  'resource_downloads',
  'contact_form_submissions',
  'site_settings'
];

const PROTECTED_TABLES = [
  'user_roles',
  'profiles',
  'login_attempts',
  'signup_attempts',
  'admin_actions'
];

export const BackupManager = () => {
  const { user, session, isAdmin, loading } = useAuth();
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [backupData, setBackupData] = useState<any>(null);
  const [restoreMode, setRestoreMode] = useState<'validate_only' | 'clear_and_restore' | 'merge'>('validate_only');
  const [selectedTables, setSelectedTables] = useState<string[]>(RESTORABLE_TABLES);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [downloadingBuckets, setDownloadingBuckets] = useState<{[key: string]: boolean}>({});

  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      if (!session) {
        toast.error("You must be logged in to create a backup");
        return;
      }

      const response = await supabase.functions.invoke('create-backup', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw response.error;
      }

      // Create a blob and download the file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Backup created successfully");
    } catch (error) {
      console.error('Backup error:', error);
      toast.error("Failed to create backup");
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleDownloadStorage = async (bucketName: string) => {
    setDownloadingBuckets(prev => ({ ...prev, [bucketName]: true }));
    try {
      const { data: files, error } = await supabase.storage.from(bucketName).list();

      if (error) throw error;

      if (!files || files.length === 0) {
        toast.info(`No files in ${bucketName} bucket`);
        return;
      }

      toast.info(`Downloading ${files.length} files from ${bucketName}...`);
      let successCount = 0;

      // Download each file with delay to prevent popup blocking
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          const { data, error: downloadError } = await supabase.storage
            .from(bucketName)
            .download(file.name);

          if (downloadError) {
            console.error(`Error downloading ${file.name}:`, downloadError);
            toast.error(`Failed to download ${file.name}`);
            continue;
          }

          if (data) {
            const url = URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            successCount++;

            // Add delay between downloads to prevent popup blocking (except for last file)
            if (i < files.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        } catch (err) {
          console.error(`Error processing ${file.name}:`, err);
        }
      }

      if (successCount === files.length) {
        toast.success(`Downloaded all ${successCount} files from ${bucketName}`);
      } else {
        toast.warning(`Downloaded ${successCount} of ${files.length} files from ${bucketName}`);
      }
    } catch (error) {
      console.error('Storage download error:', error);
      toast.error(`Failed to download from ${bucketName}`);
    } finally {
      setDownloadingBuckets(prev => ({ ...prev, [bucketName]: false }));
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      setBackupFile(file);
      setBackupData(data);
      setValidationResult(null);
      toast.success("Backup file loaded successfully");
    } catch (error) {
      console.error('Parse error:', error);
      toast.error("Invalid backup file format");
      setBackupFile(null);
      setBackupData(null);
    }
  };

  const handleValidate = async () => {
    if (!backupData) return;

    setIsRestoring(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("You must be logged in");
        return;
      }

      const response = await supabase.functions.invoke('restore-backup', {
        body: {
          backup: backupData,
          options: {
            mode: 'validate_only',
            tables_to_restore: selectedTables
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw response.error;
      }

      setValidationResult(response.data.validation);
      
      if (response.data.validation.valid) {
        toast.success("Backup validation passed!");
      } else {
        toast.error("Backup validation failed");
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error("Failed to validate backup");
    } finally {
      setIsRestoring(false);
    }
  };

  const handleRestore = async () => {
    if (!backupData || !validationResult?.valid) {
      toast.error("Please validate the backup first");
      return;
    }

    if (restoreMode === 'clear_and_restore') {
      setShowConfirmDialog(true);
      return;
    }

    await executeRestore();
  };

  const executeRestore = async () => {
    setIsRestoring(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("You must be logged in");
        return;
      }

      const response = await supabase.functions.invoke('restore-backup', {
        body: {
          backup: backupData,
          options: {
            mode: restoreMode,
            tables_to_restore: selectedTables
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw response.error;
      }

      const { restoration } = response.data;
      
      toast.success(
        `Restoration complete! Inserted ${restoration.records_inserted} records, skipped ${restoration.records_skipped}`
      );
      
      setBackupFile(null);
      setBackupData(null);
      setValidationResult(null);
    } catch (error) {
      console.error('Restore error:', error);
      toast.error("Failed to restore backup");
    } finally {
      setIsRestoring(false);
      setShowConfirmDialog(false);
    }
  };

  const toggleTable = (table: string) => {
    setSelectedTables(prev =>
      prev.includes(table)
        ? prev.filter(t => t !== table)
        : [...prev, table]
    );
  };

  const toggleAllTables = () => {
    setSelectedTables(prev =>
      prev.length === RESTORABLE_TABLES.length ? [] : [...RESTORABLE_TABLES]
    );
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Checking authentication...</span>
      </div>
    );
  }

  // Show error if not authenticated or not admin
  if (!user || !isAdmin) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Access Denied</CardTitle>
          <CardDescription>
            You must be logged in as an administrator to access backup features.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="backup" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="backup">Create Backup</TabsTrigger>
          <TabsTrigger value="restore">Restore Backup</TabsTrigger>
        </TabsList>

        <TabsContent value="backup" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Backup
          </CardTitle>
          <CardDescription>
            Export all database tables as a JSON file. Includes testimonials, services, resources, blog posts, leads, and settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleCreateBackup}
            disabled={isCreatingBackup}
            className="w-full sm:w-auto"
          >
            {isCreatingBackup ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Backup...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Create Database Backup
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Storage Files Backup
          </CardTitle>
          <CardDescription>
            Download all files from storage buckets. Each bucket will download its files individually.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Resources Bucket</p>
                <p className="text-sm text-muted-foreground">PDF guides and documents</p>
              </div>
              <Button
                onClick={() => handleDownloadStorage('resources')}
                variant="outline"
                size="sm"
                disabled={downloadingBuckets['resources']}
              >
                {downloadingBuckets['resources'] ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </>
                )}
              </Button>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Blog Images Bucket</p>
                <p className="text-sm text-muted-foreground">Featured images and blog content</p>
              </div>
              <Button
                onClick={() => handleDownloadStorage('blog-images')}
                variant="outline"
                size="sm"
                disabled={downloadingBuckets['blog-images']}
              >
                {downloadingBuckets['blog-images'] ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
        <CardHeader>
          <CardTitle className="text-amber-900 dark:text-amber-100">Backup Notes</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-amber-800 dark:text-amber-200 space-y-2">
          <p>• Store backups securely in multiple locations</p>
          <p>• Test backup restoration periodically</p>
          <p>• Secrets (API keys) are not included - document separately</p>
          <p>• Consider scheduling regular automated backups</p>
          <p>• Keep at least 3 backup copies: local, cloud, offline</p>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="restore" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Database Restoration
              </CardTitle>
              <CardDescription>
                Import and restore database tables from a backup file. Always validate before restoring.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* File Upload */}
              <div className="space-y-2">
                <Label htmlFor="backup-file">Backup File (JSON)</Label>
                <input
                  id="backup-file"
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-muted-foreground
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary file:text-primary-foreground
                    hover:file:bg-primary/90
                    cursor-pointer"
                />
                {backupFile && (
                  <p className="text-sm text-muted-foreground">
                    Loaded: {backupFile.name} ({(backupFile.size / 1024).toFixed(2)} KB)
                  </p>
                )}
              </div>

              {/* Backup Preview */}
              {backupData && (
                <div className="p-4 border rounded-lg bg-muted/50 space-y-2">
                  <h4 className="font-medium">Backup Information</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Version: {backupData.version}</p>
                    <p>Created: {new Date(backupData.created_at).toLocaleString()}</p>
                    <p>Total Tables: {backupData.metadata?.total_tables || 0}</p>
                    <p>Total Records: {backupData.metadata?.total_records || 0}</p>
                  </div>
                </div>
              )}

              {/* Restoration Mode */}
              {backupData && (
                <>
                  <div className="space-y-3">
                    <Label>Restoration Mode</Label>
                    <RadioGroup value={restoreMode} onValueChange={(value: any) => setRestoreMode(value)}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="validate_only" id="validate" />
                        <Label htmlFor="validate" className="font-normal cursor-pointer">
                          Validate Only (No changes - recommended first step)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="merge" id="merge" />
                        <Label htmlFor="merge" className="font-normal cursor-pointer">
                          Merge (Keep existing data, add new records)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="clear_and_restore" id="clear" />
                        <Label htmlFor="clear" className="font-normal cursor-pointer text-destructive">
                          Clear & Restore (⚠️ Deletes existing data first)
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Table Selection */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Tables to Restore</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleAllTables}
                      >
                        {selectedTables.length === RESTORABLE_TABLES.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-4 border rounded-lg max-h-64 overflow-y-auto">
                      {RESTORABLE_TABLES.map(table => (
                        <div key={table} className="flex items-center space-x-2">
                          <Checkbox
                            id={table}
                            checked={selectedTables.includes(table)}
                            onCheckedChange={() => toggleTable(table)}
                          />
                          <Label htmlFor={table} className="font-normal cursor-pointer">
                            {table}
                            {backupData.tables?.[table] && (
                              <span className="text-xs text-muted-foreground ml-2">
                                ({backupData.tables[table].count} records)
                              </span>
                            )}
                          </Label>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Protected tables ({PROTECTED_TABLES.join(', ')}) are excluded for security reasons
                    </p>
                  </div>

                  {/* Validation Results */}
                  {validationResult && (
                    <Alert variant={validationResult.valid ? "default" : "destructive"}>
                      {validationResult.valid ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                      <AlertTitle>
                        {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
                      </AlertTitle>
                      <AlertDescription>
                        {validationResult.valid ? (
                          <div className="space-y-1">
                            <p>Backup file is valid and ready to restore.</p>
                            {validationResult.warnings?.length > 0 && (
                              <div className="mt-2">
                                <p className="font-medium">Warnings:</p>
                                <ul className="list-disc list-inside text-sm">
                                  {validationResult.warnings.map((warning: string, i: number) => (
                                    <li key={i}>{warning}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : (
                          <ul className="list-disc list-inside">
                            {validationResult.errors?.map((error: string, i: number) => (
                              <li key={i}>{error}</li>
                            ))}
                          </ul>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={handleValidate}
                      disabled={isRestoring}
                      variant="outline"
                      className="flex-1"
                    >
                      {isRestoring ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Validating...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Validate Backup
                        </>
                      )}
                    </Button>
                    
                    <Button
                      onClick={handleRestore}
                      disabled={isRestoring || !validationResult?.valid || restoreMode === 'validate_only'}
                      className="flex-1"
                    >
                      {isRestoring ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Restoring...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Restore Backup
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}

              {/* Warning Alert */}
              <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-900 dark:text-amber-100">Important</AlertTitle>
                <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm space-y-1">
                  <p>• Always validate your backup before restoring</p>
                  <p>• Create a fresh backup before restoration to avoid data loss</p>
                  <p>• "Clear & Restore" mode will DELETE all existing data in selected tables</p>
                  <p>• Storage files must be manually re-uploaded (not included in restoration)</p>
                  <p>• Test restorations in a non-production environment first</p>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Destructive Action Warning
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="font-medium">
                This will DELETE all existing data in the selected tables before restoration!
              </p>
              <div className="p-3 bg-muted rounded-md space-y-1 text-sm">
                <p>Tables affected: {selectedTables.join(', ')}</p>
                <p>Records to restore: {backupData?.metadata?.total_records || 0}</p>
              </div>
              <p className="text-destructive font-medium">
                This action cannot be undone. Are you absolutely sure?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeRestore}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Delete and Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
